// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useMemo } from 'react';
import { useCargo } from '../contexts/CargoContext';
import { useModules } from '../contexts/ModulesContext';
import tradeGoods from '../config/TradeGoodsConfig';
import './InfoPanelTabs/CargoTab.css';

const CargoHoldView = () => {
  const { getCargoQuantity, getCargoUsed, getCargoPurchasePrice } = useCargo();
  const { getCargoCapacity } = useModules();
  const [sortBy, setSortBy] = useState('name'); // name, value, weight, profit

  const cargoCapacity = getCargoCapacity();
  const cargoUsed = getCargoUsed(tradeGoods);

  // Calculate cargo items with values
  const cargoItems = useMemo(() => {
    const items = Object.values(tradeGoods)
      .map(good => {
        const quantity = getCargoQuantity(good.id);
        if (quantity === 0) return null;

        const weight = good.weight * quantity;
        const purchasePrice = getCargoPurchasePrice ? getCargoPurchasePrice(good.id) : good.basePrice;
        const currentPrice = good.basePrice; // Use base price for now
        const totalValue = currentPrice * quantity;
        const totalCost = purchasePrice * quantity;
        const profit = totalValue - totalCost;
        const profitPercent = totalCost > 0 ? ((profit / totalCost) * 100) : 0;

        return {
          ...good,
          quantity,
          weight,
          purchasePrice,
          currentPrice,
          totalValue,
          totalCost,
          profit,
          profitPercent
        };
      })
      .filter(item => item !== null);

    // Sort items
    switch (sortBy) {
      case 'value':
        return items.sort((a, b) => b.totalValue - a.totalValue);
      case 'weight':
        return items.sort((a, b) => b.weight - a.weight);
      case 'profit':
        return items.sort((a, b) => b.profit - a.profit);
      case 'name':
      default:
        return items.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [getCargoQuantity, getCargoPurchasePrice, sortBy]);

  const totalPortfolioValue = cargoItems.reduce((sum, item) => sum + item.totalValue, 0);
  const totalPortfolioCost = cargoItems.reduce((sum, item) => sum + item.totalCost, 0);
  const totalProfit = totalPortfolioValue - totalPortfolioCost;

  return (
    <div className="cargo-tab">
      {/* Trade Goods Section */}
      <div className="cargo-section">
        <div className="cargo-section-header">
          <div className="cargo-section-title">TRADE GOODS INVENTORY</div>
          {cargoItems.length > 0 && (
            <div className="cargo-sort">
              <label>Sort by:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">Name</option>
                <option value="value">Value</option>
                <option value="weight">Weight</option>
                <option value="profit">Profit</option>
              </select>
            </div>
          )}
        </div>

        {cargoCapacity === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🚫</div>
            <div className="empty-text">No Cargo Bay Installed</div>
            <div className="empty-hint">Install a Cargo Bay module to carry trade goods</div>
          </div>
        ) : cargoItems.length > 0 ? (
          <>
            {/* Portfolio Summary */}
            <div className="portfolio-summary">
              <div className="summary-stat">
                <span className="stat-label">Total Value:</span>
                <span className="stat-value">{totalPortfolioValue.toLocaleString()} ¢</span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Total Cost:</span>
                <span className="stat-value">{totalPortfolioCost.toLocaleString()} ¢</span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Profit/Loss:</span>
                <span className={`stat-value ${totalProfit >= 0 ? 'profit' : 'loss'}`}>
                  {totalProfit >= 0 ? '+' : ''}{totalProfit.toLocaleString()} ¢
                </span>
              </div>
            </div>

            {/* Cargo Items List */}
            <div className="trade-goods-list">
              {cargoItems.map(item => (
                <div key={item.id} className="trade-good-item-display">
                  <span className="trade-good-icon">{item.icon}</span>
                  <div className="trade-good-details">
                    <div className="trade-good-name">{item.name}</div>
                    <div className="trade-good-meta">
                      Qty: {item.quantity} | Weight: {item.weight} tons
                    </div>
                    <div className="trade-good-values">
                      <span className="value-item">
                        Value: {item.totalValue.toLocaleString()} ¢
                      </span>
                      <span className="value-item">
                        Cost: {item.totalCost.toLocaleString()} ¢
                      </span>
                      <span className={`value-item profit-indicator ${item.profit >= 0 ? 'profit' : 'loss'}`}>
                        {item.profit >= 0 ? '+' : ''}{item.profit.toLocaleString()} ¢ 
                        ({item.profitPercent >= 0 ? '+' : ''}{item.profitPercent.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <div className="empty-text">No trade goods in cargo</div>
            <div className="empty-hint">Visit a trading post to buy goods</div>
          </div>
        )}
      </div>

      {/* Cargo Capacity */}
      {cargoCapacity > 0 && (
        <div className="cargo-footer">
          <div className="cargo-capacity">
            <span className="capacity-label">CARGO CAPACITY:</span>
            <span className="capacity-value">{cargoUsed}/{cargoCapacity} tons</span>
          </div>
          <div className="cargo-bar-container">
            <div className="cargo-bar">
              <div 
                className="cargo-bar-fill" 
                style={{ 
                  width: `${(cargoUsed / cargoCapacity) * 100}%`,
                  background: cargoUsed > cargoCapacity * 0.9 ? '#ff4444' : '#00ff88'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CargoHoldView;
